// Permission keys — match DB get_user_permissions() output
export const Permission = {
  Properties: 'properties',
  Units: 'units',
  Contracts: 'contracts',
  Tenants: 'tenants',
  Owners: 'owners',
  Finance: 'finance',
  Maintenance: 'maintenance',
  Reports: 'reports',
  Advertisements: 'advertisements',
  Projects: 'projects',
  Employees: 'employees',
  Tasks: 'tasks',
  Settings: 'settings',
  ServiceRequests: 'service_requests',
  Leasing: 'leasing',
} as const;

export type PermissionKey = typeof Permission[keyof typeof Permission];

// Granular permission actions for sidebar/route guards
export type PermissionAction = 'view' | 'create' | 'edit' | 'delete';
export type GranularPermissionKey = `${PermissionKey}.${PermissionAction}`;

// All permission keys as array
export const ALL_PERMISSION_KEYS: PermissionKey[] = Object.values(Permission);

/**
 * Standalone, opt-in grants outside the CRUD matrix — mirrors the API's
 * SPECIAL_PERMISSIONS. A coarse domain grant never implies them.
 */
export const SpecialPermission = {
  TasksViewAll: "tasks.view_all",
  MediaApprove: "media.approve",
  LeasingApproveDiscount: "leasing.approve_discount",
  // KYC — counted only as an EXPLICIT grant in the MAIN company (useKycComplianceAccess),
  // never through hasPermission's admin fast-path.
  ComplianceReview: "compliance.review",
  ComplianceApproveHigh: "compliance.approve_high",
} as const;
export type SpecialPermissionKey = (typeof SpecialPermission)[keyof typeof SpecialPermission];

// System roles from app_role enum in DB
export const SystemRole = {
  SuperAdmin: 'super_admin',
  Admin: 'admin',
  Employee: 'employee',
  Owner: 'owner',
  Tenant: 'tenant',
  ServiceProvider: 'service_provider',
} as const;

export type SystemRoleValue = typeof SystemRole[keyof typeof SystemRole];

// Portal-aligned custom_role values — the DB trigger
// invitations_custom_role_validate rejects role IN (owner|tenant|employee|
// service_provider) with a NULL custom_role. Every portal role must supply
// its canonical custom_role; only admin/super_admin are exempt.
export const CustomRole = {
  PropertyOwner: "property_owner",
} as const;

export type CustomRoleValue = typeof CustomRole[keyof typeof CustomRole];

// Portal types — determines which portal a user sees
export const PortalType = {
  Admin: 'admin',
  Owner: 'owner',
  Tenant: 'tenant',
  Employee: 'employee',
  Provider: 'provider',
} as const;

export type PortalTypeValue = typeof PortalType[keyof typeof PortalType];

// Map system roles to portal types
export const ROLE_TO_PORTAL: Record<SystemRoleValue, PortalTypeValue> = {
  [SystemRole.SuperAdmin]: PortalType.Admin,
  [SystemRole.Admin]: PortalType.Admin,
  [SystemRole.Employee]: PortalType.Employee,
  [SystemRole.Owner]: PortalType.Owner,
  [SystemRole.Tenant]: PortalType.Tenant,
  [SystemRole.ServiceProvider]: PortalType.Provider,
};

// Roles that bypass permission checks (get all permissions)
export const BYPASS_ROLES: SystemRoleValue[] = [
  SystemRole.SuperAdmin,
  SystemRole.Admin,
];

// Translation IDs for permission labels (from translations table seed)
export const PERMISSION_TRANSLATION_IDS: Record<PermissionKey, number> = {
  properties: 1,
  units: 2,
  contracts: 3,
  tenants: 4,
  owners: 5,
  finance: 6,
  maintenance: 7,
  reports: 8,
  advertisements: 9,
  employees: 10,
  tasks: 11,
  settings: 12,
  service_requests: 13,
  projects: 14,
  leasing: 15,
};

// Reverse map: portal type → system role (picks one canonical role per portal)
export const PORTAL_TYPE_TO_ROLE: Record<PortalTypeValue, SystemRoleValue> = {
  [PortalType.Admin]: SystemRole.Admin,
  [PortalType.Owner]: SystemRole.Owner,
  [PortalType.Tenant]: SystemRole.Tenant,
  [PortalType.Employee]: SystemRole.Employee,
  [PortalType.Provider]: SystemRole.ServiceProvider,
};

// Portal type options for select dropdowns (bilingual labels)
export const PORTAL_TYPE_OPTIONS = [
  { value: "employee", label: { ar: "موظف", en: "Employee" } },
  { value: "owner",    label: { ar: "مالك", en: "Owner" } },
  { value: "tenant",   label: { ar: "مستأجر", en: "Tenant" } },
  { value: "provider", label: { ar: "مزود خدمة", en: "Service Provider" } },
  { value: "admin",    label: { ar: "مدير", en: "Admin" } },
] as const;

export type PortalTypeOption = (typeof PORTAL_TYPE_OPTIONS)[number]["value"];

// User role assignment from junction table
export interface UserRoleAssignment {
  id: string;
  role_id: string;
  role_permissions: {
    role_name: string;
    portal_type: PortalTypeValue;
    permissions: PermissionKey[];
    name_id: number | null;
  };
}
